import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import PPTOutlinePage from './PPTOutlinePage'
import PPTResultPage from './PPTResultPage'
import { generatePPTOutline } from './pptUtils'
import { MODEL_MODES } from "../../constants/agentConfig"
import { AgentHistoryIntegration } from '../../utils/agentHistoryIntegration'
import { useCreditWarning } from '../../hooks/useCreditWarning'
import CreditWarningModal from '../../components/CreditWarningModal'
import { useSubscription } from '../../hooks/useSubscription'
import { AGENT_TYPES } from '../index.js'
import linkupService from '../../services/linkupService'

// PPT Agent States
const AGENT_STATES = {
  OUTLINE: 'outline',
  GENERATING: 'generating',
  COMPLETED: 'completed'
}

const PPTAgent = ({ query, onBack, llmService }) => {
  const [currentState, setCurrentState] = useState(AGENT_STATES.OUTLINE)
  const [outline, setOutline] = useState([])
  const [generatedContent, setGeneratedContent] = useState({})
  const [academicSources, setAcademicSources] = useState([])
  const [relevantImages, setRelevantImages] = useState([])
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hasInitialized, setHasInitialized] = useState(false)
  const { warningModal, checkAndWarnCredits, closeWarning } = useCreditWarning()
  const { consumeCredits } = useSubscription()
  
  // Use refs to track state and prevent unnecessary re-generation
  const isGeneratingOutline = useRef(false)
  const currentQuery = useRef(query)
  const hasEverGenerated = useRef(false)

  // Session storage keys for persistence
  const getSessionKey = (suffix) => `ppt_agent_${query}_${suffix}`

  // Load persisted state on component mount
  useEffect(() => {
    const loadPersistedState = () => {
      try {
        const persistedState = sessionStorage.getItem(getSessionKey('state'))
        const persistedOutline = sessionStorage.getItem(getSessionKey('outline'))
        const persistedContent = sessionStorage.getItem(getSessionKey('content'))
        const persistedSources = sessionStorage.getItem(getSessionKey('sources'))
        
        if (persistedState && persistedOutline) {
          const state = JSON.parse(persistedState)
          const outlineData = JSON.parse(persistedOutline)
          
          console.log('📋 Loading persisted PPT state:', state.currentState)
          setCurrentState(state.currentState)
          setOutline(outlineData)
          setHasInitialized(true)
          hasEverGenerated.current = true
          
          if (persistedContent) {
            const contentData = JSON.parse(persistedContent)
            setGeneratedContent(contentData)
          }
          
          if (persistedSources) {
            const sourcesData = JSON.parse(persistedSources)
            setAcademicSources(sourcesData)
          }
          
          const persistedImages = sessionStorage.getItem(getSessionKey('images'))
          if (persistedImages) {
            const imagesData = JSON.parse(persistedImages)
            setRelevantImages(imagesData)
          }
          
          // Don't generate outline if we have persisted state
          return true
        }
      } catch (error) {
        console.warn('⚠️ Failed to load persisted state:', error)
        // Clear corrupted data
        try {
          sessionStorage.removeItem(getSessionKey('state'))
          sessionStorage.removeItem(getSessionKey('outline'))
          sessionStorage.removeItem(getSessionKey('content'))
          sessionStorage.removeItem(getSessionKey('sources'))
          sessionStorage.removeItem(getSessionKey('images'))
        } catch (clearError) {
          console.warn('⚠️ Failed to clear corrupted session data:', clearError)
        }
      }
      return false
    }
    
    // Only load persisted state if query matches current query
    if (currentQuery.current === query) {
      const hasPersistedState = loadPersistedState()
      if (hasPersistedState) {
        return // Skip outline generation
      }
    } else {
      // Query changed, clear old session data
      try {
        const oldQuery = currentQuery.current
        sessionStorage.removeItem(`ppt_agent_${oldQuery}_state`)
        sessionStorage.removeItem(`ppt_agent_${oldQuery}_outline`)
        sessionStorage.removeItem(`ppt_agent_${oldQuery}_content`)
        sessionStorage.removeItem(`ppt_agent_${oldQuery}_sources`)
        sessionStorage.removeItem(`ppt_agent_${oldQuery}_images`)
      } catch (error) {
        console.warn('⚠️ Failed to clear old session data:', error)
      }
      currentQuery.current = query
    }
    
    // Generate initial outline only if needed
    if (query && !hasInitialized && !isGeneratingOutline.current && !hasEverGenerated.current) {
      generateInitialOutline()
    }
  }, [query])

  // Persist state changes to session storage
  useEffect(() => {
    if (hasInitialized && outline.length > 0) {
      try {
        sessionStorage.setItem(getSessionKey('state'), JSON.stringify({
          currentState,
          hasInitialized: true
        }))
        sessionStorage.setItem(getSessionKey('outline'), JSON.stringify(outline))
        
        if (Object.keys(generatedContent).length > 0) {
          sessionStorage.setItem(getSessionKey('content'), JSON.stringify(generatedContent))
        }
        
        if (academicSources.length > 0) {
          sessionStorage.setItem(getSessionKey('sources'), JSON.stringify(academicSources))
        }
        
        if (relevantImages.length > 0) {
          sessionStorage.setItem(getSessionKey('images'), JSON.stringify(relevantImages))
        }
      } catch (error) {
        console.warn('⚠️ Failed to persist state:', error)
      }
    }
  }, [currentState, outline, generatedContent, academicSources, relevantImages, hasInitialized, query])

  const generateInitialOutline = async () => {
    if (isGeneratingOutline.current || hasEverGenerated.current) {
      console.log('⚠️ Outline generation already in progress or completed, skipping...')
      return
    }
    
    isGeneratingOutline.current = true
    
    try {
      setIsLoading(true)
      setError(null)
      console.log('🔄 Generating initial outline for:', query)
      
      const result = await generatePPTOutline(query, llmService)
      setOutline(result.outline)
      setAcademicSources(result.academicSources)
      setRelevantImages(result.relevantImages || [])
      setHasInitialized(true)
      hasEverGenerated.current = true
      
      console.log('✅ Initial outline generated successfully')
    } catch (error) {
      console.error('❌ Error generating initial outline:', error)
      setError('Gagal membuat outline awal')
      setHasInitialized(false)
      hasEverGenerated.current = false // Allow retry on error
    } finally {
      setIsLoading(false)
      isGeneratingOutline.current = false
    }
  }

  const handleStartGeneration = async (finalOutline) => {
    // Check credits before starting generation
    const canProceed = await checkAndWarnCredits(AGENT_TYPES.PPT)
    if (!canProceed) {
      return // Stop execution if insufficient credits
    }

    setCurrentState(AGENT_STATES.GENERATING)
    setOutline(finalOutline)
    setProgress({ current: 0, total: finalOutline.length })
    setIsLoading(true)
    setError(null)
    
    try {
      console.log(`🔄 Memulai generate presentasi PPT untuk topik: ${query}`)
      console.log(`📝 Outline terdiri dari ${finalOutline.length} slide`)
      
      const newGeneratedContent = {}
      
      // Generate content for each slide
      for (let i = 0; i < finalOutline.length; i++) {
        const slide = finalOutline[i]
        setProgress({ current: i, total: finalOutline.length })
        
        console.log(`📄 Generating slide ${i + 1}/${finalOutline.length}: ${slide.title}`)
        
        try {
          const slideContent = await generateSlideContent(slide, query, llmService, academicSources, relevantImages)
          newGeneratedContent[slide.id] = slideContent
          setGeneratedContent(prev => ({ ...prev, [slide.id]: slideContent }))
        } catch (slideError) {
          console.error(`❌ Error generating slide ${slide.title}:`, slideError)
          newGeneratedContent[slide.id] = {
            content: `Error generating content for ${slide.title}`,
            visualElements: [],
            speakerNotes: 'Content generation failed'
          }
        }
      }
      
      setProgress({ current: finalOutline.length, total: finalOutline.length })
      setCurrentState(AGENT_STATES.COMPLETED)
      
      // Consume credits after successful generation
      await consumeCredits(AGENT_TYPES.PPT)
      
      // Save to agent history
      try {
        const saveResult = await AgentHistoryIntegration.savePPTResult({
          query: query,
          outline: finalOutline,
          generatedContent: newGeneratedContent,
          academicSources: academicSources,
          progress: { current: finalOutline.length, total: finalOutline.length }
        })
        
        if (saveResult.success) {
          console.log('✅ PPT berhasil disimpan ke history:', saveResult.data)
        } else {
          console.warn('⚠️ Gagal menyimpan PPT ke history:', saveResult.error)
        }
      } catch (historyError) {
        console.warn('⚠️ Failed to save to agent history:', historyError)
      }
      
      console.log('✅ PPT generation completed successfully')
    } catch (error) {
      console.error('❌ Error during PPT generation:', error)
      setError('Terjadi kesalahan saat membuat presentasi')
      setCurrentState(AGENT_STATES.OUTLINE)
    } finally {
      setIsLoading(false)
    }
  }

  const generateSlideContent = async (slide, topic, llmService, sources, images = []) => {
    const sourceContext = sources.slice(0, 5).map((source, index) => 
      `${index + 1}. ${source.title}: ${source.snippet}`
    ).join('\n')

    // Gunakan gambar yang sudah didapat dari generatePPTOutline
    const slideRelevantImages = images.slice(0, 3)

    const prompt = `Buatkan konten slide presentasi PowerPoint untuk:
Topik: ${topic}
Slide: ${slide.title}
Deskripsi: ${slide.description}

Format JSON yang diinginkan:
{
  "content": {
    "title": "Judul Slide",
    "mainPoints": ["Poin utama 1", "Poin utama 2", "Poin utama 3"],
    "details": "Penjelasan detail untuk slide ini"
  },
  "executiveSummary": "Ringkasan eksekutif yang powerful dan actionable untuk slide ini",
  "statistics": [
    {
      "label": "Statistik Kunci 1",
      "value": "75%",
      "description": "Deskripsi statistik yang relevan"
    },
    {
      "label": "Statistik Kunci 2",
      "value": "120+",
      "description": "Data pendukung yang signifikan"
    }
  ],
  "keyTakeaways": [
    {
      "title": "Key Takeaway 1",
      "description": "Insight penting yang harus diingat",
      "impact": "high"
    },
    {
      "title": "Key Takeaway 2",
      "description": "Pembelajaran kunci dari analisis",
      "impact": "medium"
    }
  ],
  "recommendations": [
    {
      "title": "Rekomendasi Strategis 1",
      "description": "Langkah konkret yang harus diambil",
      "priority": "high",
      "timeline": "short-term"
    },
    {
      "title": "Rekomendasi Strategis 2",
      "description": "Inisiatif jangka panjang yang diperlukan",
      "priority": "medium",
      "timeline": "long-term"
    }
  ],
  "visualElements": [
    {
      "type": "chart",
      "title": "Radar Chart Analisis Kompetitif",
      "description": "Multi-dimensional competitive analysis dengan 6 parameter kunci",
      "data": {
        "labels": ["Inovasi", "Kualitas", "Harga", "Layanan", "Brand", "Distribusi"],
        "values": [85, 78, 65, 90, 82, 75],
        "chartType": "radar"
      }
    },
    {
      "type": "chart",
      "title": "Scatter Plot Market Positioning",
      "description": "Positioning kompetitor berdasarkan harga vs kualitas",
      "data": {
        "labels": ["Kompetitor A", "Kompetitor B", "Kompetitor C", "Kami"],
        "values": [75, 85, 60, 90],
        "chartType": "scatter"
      }
    },
    {
      "type": "chart",
      "title": "Area Chart Timeline Adopsi",
      "description": "Evolution of technology adoption over time",
      "data": {
        "labels": ["Q1 2023", "Q2 2023", "Q3 2023", "Q4 2023", "Q1 2024"],
        "values": [15, 28, 45, 62, 78],
        "chartType": "area"
      }
    },
    {
      "type": "chart",
      "title": "Polar Area Distribution",
      "description": "Segmentasi pasar berdasarkan demografi",
      "data": {
        "labels": ["Gen Z", "Millennial", "Gen X", "Baby Boomer"],
        "values": [35, 40, 20, 5],
        "chartType": "polarArea"
      }
    }${relevantImages.length > 0 ? `,
    {
      "type": "image",
      "title": "${relevantImages[0].title}",
      "description": "${relevantImages[0].description}",
      "data": {
        "url": "${relevantImages[0].url}",
        "imageUrl": "${relevantImages[0].url}"
      }
    }` : ''}
  ],
  "speakerNotes": "Catatan untuk presenter"
}

PETUNJUK PENTING untuk visualElements:
- WAJIB buat 1-3 visual elements per slide dengan VARIASI CHART YANG MENARIK
- GUNAKAN CHART TYPES YANG BERVARIASI sesuai PptxGenJS:
  * "radar" - untuk analisis multi-dimensional, kompetitif, 360-degree view
  * "scatter" - untuk positioning, correlation, bubble analysis, matrix
  * "area" - untuk timeline, evolution, adoption trends, growth
  * "polarArea" - untuk segmentasi, distribution, market share
  * "pie" - untuk proporsi sederhana, market share
  * "doughnut" - untuk nested data, 3D visualization
  * "line" - untuk trend, multi-series, growth patterns
  * "bar" - untuk comparison, stacked data, multi-axis

- PILIH CHART TYPE berdasarkan KONTEN SLIDE:
  * Analisis kompetitif → radar
  * Market positioning → scatter
  * Timeline/evolusi → area
  * Segmentasi pasar → polarArea
  * Perbandingan → bar
  * Trend pertumbuhan → line
  * Proporsi → pie/doughnut

- Untuk data chart:
  * labels: 3-6 kategori yang relevan
  * values: angka realistis sesuai konteks
  * chartType: PILIH yang paling sesuai dengan data dan tujuan

- Untuk type "diagram":
  * data.steps: proses bertahap yang logis
  * Buat alur yang mudah dipahami

- Untuk type "image" (jika ada gambar relevan):
  * Gunakan gambar dari hasil pencarian
  * Berikan title dan description yang informatif

- Pastikan SETIAP visual element memiliki:
  * Title yang deskriptif
  * Description yang menjelaskan insight
  * Data yang relevan dan realistis

Tipe visual elements: chart (PRIORITAS), diagram, infographic, image

Referensi:\n${sourceContext}

${slideRelevantImages.length > 0 ? `\nGambar tersedia:\n${slideRelevantImages.map((img, i) => `${i+1}. ${img.title}: ${img.description}`).join('\n')}` : ''}

Berikan HANYA JSON tanpa penjelasan tambahan:`

    try {
      const response = await llmService.sendMessage(
        [{ role: 'user', content: prompt }],
        { 
          mode: MODEL_MODES.GENIUS,
          searchType: 'web',
          stream: false,
          consumeCredits: false
        }
      )
      
      const cleanedResponse = response.replace(/```json|```/g, '').trim()
      const parsedContent = JSON.parse(cleanedResponse)
      
      // Validate and sanitize the parsed content
      const sanitizedContent = {
        content: {
          title: typeof parsedContent.content?.title === 'string' ? parsedContent.content.title : slide.title,
          mainPoints: Array.isArray(parsedContent.content?.mainPoints) ? 
            parsedContent.content.mainPoints.filter(point => typeof point === 'string') : [],
          details: typeof parsedContent.content?.details === 'string' ? parsedContent.content.details : slide.description
        },
        executiveSummary: typeof parsedContent.executiveSummary === 'string' ? parsedContent.executiveSummary : null,
        statistics: Array.isArray(parsedContent.statistics) ? 
          parsedContent.statistics.filter(stat => stat && stat.label && stat.value) : null,
        keyTakeaways: Array.isArray(parsedContent.keyTakeaways) ? 
          parsedContent.keyTakeaways.filter(takeaway => takeaway && takeaway.title) : null,
        recommendations: Array.isArray(parsedContent.recommendations) ? 
          parsedContent.recommendations.filter(rec => rec && rec.title) : null,
        visualElements: Array.isArray(parsedContent.visualElements) ? 
          parsedContent.visualElements.map(element => {
            const sanitizedElement = {
              type: typeof element.type === 'string' ? element.type : 'text',
              title: typeof element.title === 'string' ? element.title : 'Visual Element',
              description: typeof element.description === 'string' ? element.description : ''
            }
            
            // Handle different data structures based on element type
            if (element.type === 'chart' && element.data && typeof element.data === 'object') {
              // Validate chart data structure
              let labels = Array.isArray(element.data.labels) ? element.data.labels : ['Kategori A', 'Kategori B', 'Kategori C', 'Kategori D']
              let values = Array.isArray(element.data.values) ? 
                element.data.values.filter(val => typeof val === 'number' && val >= 0) : [25, 35, 25, 15]
              
              // Ensure labels and values have same length
              if (labels.length !== values.length) {
                const minLength = Math.min(labels.length, values.length)
                labels = labels.slice(0, minLength)
                values = values.slice(0, minLength)
                
                // If still empty, provide defaults
                if (labels.length === 0) {
                  labels = ['Data 1', 'Data 2', 'Data 3']
                  values = [30, 40, 30]
                }
              }
              
              const chartData = {
                labels: labels,
                values: values,
                chartType: ['pie', 'bar', 'line', 'doughnut'].includes(element.data.chartType) ? 
                  element.data.chartType : 'pie'
              }
              sanitizedElement.data = chartData
            } else if (element.type === 'diagram' && element.data && typeof element.data === 'object') {
              // Validate diagram data structure
              let diagramData = {}
              
              // Handle steps format (preferred)
              if (Array.isArray(element.data.steps)) {
                diagramData.steps = element.data.steps.filter(step => 
                  step && (step.title || step.text || step.name)
                ).map(step => ({
                  title: step.title || step.text || step.name || 'Step',
                  description: step.description || step.desc || ''
                }))
              }
              // Handle elements format (fallback)
              else if (Array.isArray(element.data.elements)) {
                diagramData.steps = element.data.elements.filter(el => 
                  el && (el.text || el.title || el.name)
                ).map((el, index) => ({
                  title: el.text || el.title || el.name || `Step ${index + 1}`,
                  description: el.description || el.desc || ''
                }))
              }
              // Default fallback
              else {
                diagramData.steps = [
                  {title: 'Langkah 1', description: 'Tahap pertama proses'},
                  {title: 'Langkah 2', description: 'Tahap kedua proses'},
                  {title: 'Langkah 3', description: 'Tahap ketiga proses'}
                ]
              }
              
              sanitizedElement.data = diagramData
            } else if (element.type === 'infographic' && element.data && typeof element.data === 'object') {
              // Validate infographic data structure
              const infographicData = {
                sections: Array.isArray(element.data.sections) ? 
                  element.data.sections.filter(section => section && section.title).map(section => ({
                    title: section.title,
                    value: section.value || '',
                    description: section.description || '',
                    icon: section.icon || 'chart'
                  })) : [
                    {title: 'Statistik 1', value: '75%', description: 'Deskripsi statistik', icon: 'chart'},
                    {title: 'Statistik 2', value: '120+', description: 'Deskripsi lainnya', icon: 'trending'},
                    {title: 'Statistik 3', value: '50M', description: 'Data tambahan', icon: 'target'}
                  ]
              }
              sanitizedElement.data = infographicData
            } else {
              // For other types, provide meaningful fallback
              if (element.type === 'chart') {
                sanitizedElement.data = {
                  labels: ['Sample A', 'Sample B', 'Sample C'],
                  values: [40, 35, 25],
                  chartType: 'pie'
                }
              } else if (element.type === 'diagram') {
                sanitizedElement.data = {
                  steps: [
                    {title: 'Tahap Awal', description: 'Memulai proses'},
                    {title: 'Tahap Tengah', description: 'Mengembangkan ide'},
                    {title: 'Tahap Akhir', description: 'Menyelesaikan hasil'}
                  ]
                }
              } else {
                sanitizedElement.data = element.data || ''
              }
            }
            
            return sanitizedElement
          }) : [],
        speakerNotes: typeof parsedContent.speakerNotes === 'string' ? parsedContent.speakerNotes : 'Catatan presenter'
      }
      
      return sanitizedContent
    } catch (error) {
      console.warn('❌ Error generating slide content, using fallback:', error)
      return {
        content: {
          title: slide.title,
          mainPoints: ["Poin utama akan diisi", "Konten sedang diproses", "Silakan refresh untuk mencoba lagi"],
          details: slide.description
        },
        executiveSummary: null,
        statistics: null,
        keyTakeaways: null,
        recommendations: null,
        visualElements: [],
        speakerNotes: "Catatan presenter akan diisi"
      }
    }
  }

  const handleEditOutline = () => {
    setCurrentState(AGENT_STATES.OUTLINE)
  }

  const handleRegenerateSlide = async (slideId) => {
    const slide = outline.find(s => s.id === slideId)
    if (!slide) return

    try {
      setIsLoading(true)
      const newContent = await generateSlideContent(slide, query, llmService, academicSources, relevantImages)
      setGeneratedContent(prev => ({ ...prev, [slideId]: newContent }))
    } catch (error) {
      console.error('❌ Error regenerating slide:', error)
      setError('Gagal regenerate slide')
    } finally {
      setIsLoading(false)
    }
  }

  // Render based on current state
  switch (currentState) {
    case AGENT_STATES.OUTLINE:
      return (
        <>
          <PPTOutlinePage
            query={query}
            outline={outline}
            onStartGeneration={handleStartGeneration}
            onBack={onBack}
            isLoading={isLoading}
            error={error}
          />
          {warningModal.isOpen && (
            <CreditWarningModal
              isOpen={warningModal.isOpen}
              onClose={closeWarning}
              requiredCredits={warningModal.requiredCredits}
              currentCredits={warningModal.currentCredits}
              agentType={warningModal.agentType}
            />
          )}
        </>
      )
    
    case AGENT_STATES.GENERATING:
    case AGENT_STATES.COMPLETED:
      return (
        <PPTResultPage
          query={query}
          outline={outline}
          generatedContent={generatedContent}
          academicSources={academicSources}
          progress={progress}
          isLoading={isLoading}
          isCompleted={currentState === AGENT_STATES.COMPLETED}
          onBack={onBack}
          onEditOutline={handleEditOutline}
          onRegenerateSlide={handleRegenerateSlide}
          error={error}
        />
      )
    
    default:
      return null
  }
}

PPTAgent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object.isRequired
}

export default PPTAgent